import { GRID } from '../config/constants';
import { snapToGrid as snapPointToGrid, zoomAtPoint } from './viewport';

export interface ViewportPoint {
  x: number;
  y: number;
}

export interface ViewportContextBase {
  render: () => void;
  notifyChange: () => void;
}

export interface ViewportSnapshot {
  panX: number;
  panY: number;
  scale: number;
  inverted: boolean;
  gridEnabled: boolean;
  gridSize: number;
}

interface ViewportInitialState {
  gridEnabled?: boolean;
  snapEnabled?: boolean;
  gridSize?: number;
  inverted?: boolean;
}

interface ViewportStateUpdate {
  scale?: number;
  panX?: number;
  panY?: number;
  gridEnabled?: boolean;
  snapEnabled?: boolean;
  inverted?: boolean;
  gridSize?: number;
  panModeEnabled?: boolean;
}

export abstract class ViewportControllerBase<TContext extends ViewportContextBase> {
  protected readonly context: TContext;
  protected scale = 1;
  protected panX = 0;
  protected panY = 0;
  protected gridEnabled: boolean;
  protected snapEnabled: boolean;
  protected gridSize: number;
  protected inverted: boolean;
  protected panModeEnabled = false;
  private onViewChangeCallback: ((scale: number) => void) | null = null;
  protected onToggleChangeCallback:
    | ((grid: boolean, snap: boolean, invert: boolean) => void)
    | null = null;

  constructor(context: TContext, initialState: ViewportInitialState = {}) {
    this.context = context;
    this.gridEnabled = initialState.gridEnabled ?? false;
    this.snapEnabled = initialState.snapEnabled ?? false;
    this.gridSize = initialState.gridSize ?? GRID.defaultSize;
    this.inverted = initialState.inverted ?? false;
  }

  setOnViewChange(callback: (scale: number) => void): void {
    this.onViewChangeCallback = callback;
  }

  setOnToggleChange(callback: (grid: boolean, snap: boolean, invert: boolean) => void): void {
    this.onToggleChangeCallback = callback;
  }

  notifyViewChange(): void {
    this.onViewChangeCallback?.(this.scale);
  }

  protected notifyToggleChange(): void {
    this.onToggleChangeCallback?.(this.gridEnabled, this.snapEnabled, this.inverted);
  }

  getScale(): number {
    return this.scale;
  }

  setScale(value: number): void {
    this.scale = value;
  }

  getPan(): { panX: number; panY: number } {
    return { panX: this.panX, panY: this.panY };
  }

  getPanScale(): { panX: number; panY: number; scale: number } {
    return { panX: this.panX, panY: this.panY, scale: this.scale };
  }

  setPan(panX: number, panY: number): void {
    this.panX = panX;
    this.panY = panY;
  }

  panBy(deltaX: number, deltaY: number): void {
    this.panX += deltaX;
    this.panY += deltaY;
    this.context.render();
  }

  zoomAtPoint(screenPoint: ViewportPoint, factor: number): void {
    const zoomed = zoomAtPoint(
      { panX: this.panX, panY: this.panY, scale: this.scale },
      screenPoint,
      factor,
      0.1,
      5
    );
    this.panX = zoomed.panX;
    this.panY = zoomed.panY;
    this.scale = zoomed.scale;
    this.context.render();
    this.notifyViewChange();
  }

  setPanModeEnabled(enabled: boolean): void {
    this.panModeEnabled = enabled;
  }

  isPanModeEnabled(): boolean {
    return this.panModeEnabled;
  }

  setGridEnabled(enabled: boolean): void {
    if (this.gridEnabled === enabled) return;
    this.gridEnabled = enabled;
    this.context.render();
    this.notifyToggleChange();
    this.context.notifyChange();
  }

  isGridEnabled(): boolean {
    return this.gridEnabled;
  }

  setSnapEnabled(enabled: boolean): void {
    if (this.snapEnabled === enabled) return;
    this.snapEnabled = enabled;
    this.notifyToggleChange();
    this.context.notifyChange();
  }

  isSnapEnabled(): boolean {
    return this.snapEnabled;
  }

  setGridSize(size: number): void {
    this.gridSize = Math.max(GRID.minSize, Math.min(GRID.maxSize, size));
    this.context.render();
  }

  getGridSize(): number {
    return this.gridSize;
  }

  snapToGrid<T extends ViewportPoint>(point: T): T {
    if (!this.snapEnabled) return point;
    return snapPointToGrid(point, this.gridSize);
  }

  abstract setInverted(enabled: boolean): void;

  isInverted(): boolean {
    return this.inverted;
  }

  applyState(state: ViewportStateUpdate): void {
    if (state.scale !== undefined) this.scale = state.scale;
    if (state.panX !== undefined) this.panX = state.panX;
    if (state.panY !== undefined) this.panY = state.panY;
    if (state.gridEnabled !== undefined) this.gridEnabled = state.gridEnabled;
    if (state.snapEnabled !== undefined) this.snapEnabled = state.snapEnabled;
    if (state.inverted !== undefined) this.inverted = state.inverted;
    if (state.gridSize !== undefined) this.gridSize = state.gridSize;
    if (state.panModeEnabled !== undefined) this.panModeEnabled = state.panModeEnabled;
  }
}
